<script setup lang="ts">
import type { VideoProgressBarProps } from '.';

import { useEventListener } from '@vueuse/core';

const props = withDefaults(defineProps<VideoProgressBarProps>(), {
	disableBackdropBlur: false
});

const emit = defineEmits<{
	seek: [ratio: number];
	'seek-start': [];
	'seek-end': [];
}>();

const progressBarRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);

function emitSeekFromPosition(clientX: number) {
	if (!progressBarRef.value) return;

	const rect = progressBarRef.value.getBoundingClientRect();
	const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));

	emit('seek', ratio);
}

function handleDragStart(event: MouseEvent | TouchEvent) {
	event.stopPropagation();
	event.preventDefault();

	const clientX =
		'touches' in event ? event.touches[0]?.clientX : event.clientX;

	if (clientX === undefined) return;

	isDragging.value = true;
	emit('seek-start');
	emitSeekFromPosition(clientX);
}

function handleDragEnd() {
	if (!isDragging.value) return;

	isDragging.value = false;
	emit('seek-end');
}

useEventListener(
	() => (isDragging.value ? document : null),
	'mousemove',
	(event: MouseEvent) => {
		event.preventDefault();
		emitSeekFromPosition(event.clientX);
	}
);

useEventListener(
	() => (isDragging.value ? document : null),
	'touchmove',
	(event: TouchEvent) => {
		event.preventDefault();

		const touch = event.touches[0];
		if (touch) {
			emitSeekFromPosition(touch.clientX);
		}
	},
	{ passive: false }
);

useEventListener(
	() => (isDragging.value ? document : null),
	'mouseup',
	handleDragEnd
);

useEventListener(
	() => (isDragging.value ? document : null),
	'touchend',
	handleDragEnd
);

useEventListener(
	() => (isDragging.value ? document : null),
	'touchcancel',
	handleDragEnd
);
</script>

<template>
	<div
		class="relative w-full cursor-pointer select-none py-3 touch-none"
		@mousedown.stop="handleDragStart"
		@touchstart.stop.prevent="handleDragStart"
	>
		<div
			ref="progressBarRef"
			:class="[
				'relative h-1 w-full overflow-hidden rounded-full bg-white/30',
				props.disableBackdropBlur ? '' : 'backdrop-blur-sm'
			]"
		>
			<div
				class="absolute left-0 top-0 h-1 rounded-full bg-white"
				:class="isDragging ? '' : 'transition-[width] duration-200'"
				:style="{ width: `${progress}%` }"
			/>
		</div>
	</div>
</template>
